import { useMemo } from 'react';
import { ServiceRotationOption } from '@mark43/cad-api';
import { Divider, HStack, Icon, VStack, Box, Text } from '@cad-arc';
import { ServiceRotationFormType } from './types';
import { useFormContext, useWatch } from 'react-hook-form';

export function ServiceProviderSelect({
    value,
    setValue,
    options,
}: {
    value: number;
    setValue: (option?: ServiceRotationOption) => void;
    options: ServiceRotationOption[];
}) {
    const form = useFormContext<ServiceRotationFormType>();
    const selectedCapability = useWatch({
        control: form.control,
        name: 'serviceRotationTypeCapability',
    });
    const filteredOptions = useMemo(() => {
        if (selectedCapability) {
            return options.filter((option) => {
                return option.serviceRotationTypeCapabilityIds.includes(selectedCapability.value);
            });
        } else {
            return options;
        }
    }, [options, selectedCapability]);
    return (
        <VStack
            align="left"
            p="2"
            rounded="md"
            w="full"
            bg="surface.background"
            data-testid="SERVICE_ROTATION_OPTIONS"
            gap="2px"
        >
            <Text variant="headingXs">Service Providers</Text>
            <Divider />
            <ProvidersList value={value} setValue={setValue} options={filteredOptions} />
        </VStack>
    );
}

function ProvidersList({
    value,
    setValue,
    options,
}: {
    value: number;
    setValue: (option: ServiceRotationOption) => void;
    options: ServiceRotationOption[];
}) {
    return (
        <Box w="full" maxHeight="175px" overflowY="auto">
            {options.map((option, i) => {
                const isSelected = option.value === value;
                return (
                    <HStack
                        key={option.value}
                        role="group"
                        _hover={{ bg: 'surface.secondary' }}
                        onClick={() => {
                            setValue(option);
                        }}
                        cursor="pointer"
                        py="1"
                    >
                        <Box display="flex" alignItems="center" justifyContent="center" w="8">
                            {isSelected && (
                                <Icon icon="Check" size="sm" color="selected.emphasis" />
                            )}
                        </Box>
                        <Text data-index={i} data-selected={isSelected} variant="bodyMd">
                            {option.label}
                        </Text>
                    </HStack>
                );
            })}
        </Box>
    );
}
